import React, { useEffect, useState } from "react";
import ReportCardSidebar from "./components/report-card-sidebar/report-card-sidebar";
import styles from "./student-report-card.module.css";
import CustomTextfield from "../../../../../../common/input/custom-textfield/custom-textfield";
import { SearchOutlined } from "@ant-design/icons";
import { getReportCardList } from "../../../../../../services/reportCard";
import moment from "moment";
import { fetchTerms } from "../../../../../../services/school_info";
import { createAntdSelection } from "../../../../../../utils/common";
import StudentExamReport from "./components/student-exam-report/student-exam-report";
import { fetchStudentsAcademicYear } from "../../../../../../services/class_routine";

const StudentReportCard = ({ studentID }) => {
  const [search, setSearch] = useState("");
  const [reportCardListing, setReportCardListing] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [loading, setLoading] = useState(false);
  const [yearLoading, setYearLoading] = useState(false);
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [hasMoreYear, setHasMoreYear] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearList, setYearList] = useState([]);
  const [isEcdStudent, setIsEcdStudent] = useState(false);
  const PAGE_SIZE = 20;

  const getReportCardListing = async () => {
    setLoading(true);
    try {
      const params = {
        student_id: studentID,
        limit: 100,
        offset: 0,
        academic_year_id: selectedYear,
        term_id: selectedTerm,
        sort_by: "date",
        sort_order: "desc",
        status: "Completed",
        search_key: search,
      };
      setReportCardListing([]);
      setSelectedExam(null);

      const response = await getReportCardList(params);
      if (response?.data) {
        const formatted = response.data?.data?.exams?.map((item) => {
          const subjectSet = new Set();

          item?.exam_details?.forEach((detail) => {
            const name = detail?.subject_name?.trim();
            if (name) {
              const formattedName =
                name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
              subjectSet.add(formattedName);
            }
          });

          return {
            id: item?.id,
            title: item?.exam_name,
            subjects: Array.from(subjectSet),
            date:
              item?.exam_start_date ||
              moment(item?.created_at).format("Do MMMM, YYYY"),
          };
        });
        setReportCardListing(formatted);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error?.data?.message);
    }
  };

  const getAcademicYearList = async (tempPage = 1, append = false) => {
    try {
      setYearLoading(true);
      const payload = {
        page: tempPage,
        pageSize: PAGE_SIZE,
        student_id: studentID,
      };
      const response = await fetchStudentsAcademicYear(payload);
      if (response?.data) {
        const years = response?.data?.data?.academicYears || [];
        const totalCount = response?.data?.data?.totalRecords || 0;

        const reachedEnd = tempPage * PAGE_SIZE >= totalCount;
        setHasMoreYear(!reachedEnd);
        setYearList((prev) => (append ? [...prev, ...years] : years));

        const active = years.find((y) => y?.active_year);
        setSelectedYear(active?.id || null);
        setIsEcdStudent(active?.student_info?.is_ecd);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setYearLoading(false);
    }
  };

  const onPopupScroll = (e) => {
    const el = e.target;
    const reachedBottom =
      el.scrollTop + el.offsetHeight >= el.scrollHeight - 10;

    if (reachedBottom && !loading && hasMoreYear) {
      const nextPage = pageNo + 1;
      setPageNo(nextPage);
      getAcademicYearList(nextPage, true);
    }
  };

  const onChangeSelectedYear = (id) => {
    setPageNo(1);
    setSelectedYear(id);

    const active = yearList?.find((y) => y?.id === id);
    setIsEcdStudent(active?.student_info?.is_ecd);
  };

  useEffect(() => {
    getAcademicYearList();
  }, []);

  useEffect(() => {
    setReportCardListing([]);
    setPageNo(1);
    if (selectedTerm && !isEcdStudent) {
      getReportCardListing();
    }
  }, [selectedTerm, studentID, search, selectedYear]);

  useEffect(() => {
    if (selectedYear) {
      getTerms();
    }
  }, [selectedYear]);

  const getTerms = async () => {
    setLoading(true);
    try {
      const params = {
        academic_year_id: selectedYear,
      };
      const resp = await fetchTerms(params);
      const data = resp?.data?.data?.terms;
      const selectable = createAntdSelection(data);
      setTerms(selectable);
      setSelectedTerm(selectable[0]?.value);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExamSelect = (exam) => {
    setSelectedExam(exam);
  };

  return (
    <div className={styles["report-container"]}>
      <div className={styles["heading-wrapper"]}>
        <div className={styles["heading"]}>Report Card</div>
        <div className={styles["search-bar-wrapper"]}>
          <CustomTextfield
            id="exam"
            placeholder="Search by exam"
            size="large"
            onChange={(e) => setSearch(e.target.value)}
            className="search-bar"
            prefix={<SearchOutlined style={{ color: "#98A2B3" }} />}
          />
        </div>
      </div>
      <div className={styles["exam-report-container"]}>
        <ReportCardSidebar
          filteredExams={reportCardListing}
          onExamSelect={handleExamSelect}
          sort={selectedTerm}
          setSort={setSelectedTerm}
          loading={loading || yearLoading}
          termList={terms}
          selectedYear={selectedYear}
          changeSelectedYear={onChangeSelectedYear}
          onPopupScroll={onPopupScroll}
          yearList={yearList}
          studentID={studentID}
          isEcdStudent={isEcdStudent}
        />

        <div className={styles["exam-report-right-section"]}>
          {!isEcdStudent && !selectedExam ? (
            <h2>Select an exam to view report</h2>
          ) : isEcdStudent && !selectedTerm ? (
            <h2>Required a term to view report</h2>
          ) : (
            <StudentExamReport 
              selectedExam={selectedExam} 
              studentID={studentID}
              selectedYear={selectedYear}
              isEcdStudent={isEcdStudent}
              selectedTerm={selectedTerm}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentReportCard;
